import type { Schedule } from "@poi-planner/shared";
import { normalizeTripTimezoneIds } from "@poi-planner/shared";
import { parseZoned, zonedFromDateAndTime } from "./zonedTime";

function timezoneName(
  zone: string,
  date: Date,
  style: "short" | "longGeneric",
) {
  return (
    new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      timeZoneName: style,
    })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value ?? zone
  );
}

export function timezoneCity(zone: string): string {
  if (zone === "UTC") return "UTC";
  return (zone.split("/").at(-1) ?? zone).replaceAll("_", " ");
}

export function timezoneRegion(zone: string): string {
  return zone.includes("/") ? zone.split("/")[0] : "Global";
}

export function timezoneDateInstant(zone: string, date: string): Date {
  const value = zonedFromDateAndTime(date, "12:00", zone);
  return new Date(parseZoned(value).epochMilliseconds);
}

export function timezoneOffset(zone: string, date: string): string {
  return parseZoned(zonedFromDateAndTime(date, "12:00", zone)).offset;
}

export function timezoneAbbreviation(zone: string, date: string): string {
  return timezoneName(zone, timezoneDateInstant(zone, date), "short");
}

export function timezoneLongName(zone: string, date: string): string {
  return timezoneName(zone, timezoneDateInstant(zone, date), "longGeneric");
}

export function formatTimezoneLabel(zone: string, date: string): string {
  const abbreviation = timezoneAbbreviation(zone, date);
  const offset = timezoneOffset(zone, date);
  return `${timezoneCity(zone)} — ${abbreviation} (UTC${offset})`;
}

export function collectUsedTimezoneIds(
  defaultTimezone: string,
  schedule: Schedule,
): string[] {
  const values: string[] = [
    ...Object.values(schedule.timezoneOverrides),
    ...schedule.scheduledPOIs.flatMap((item) => {
      try {
        return [
          parseZoned(item.startTime).timeZoneId,
          parseZoned(item.endTime).timeZoneId,
        ];
      } catch {
        return [];
      }
    }),
    ...schedule.notes.flatMap((note) => {
      try {
        return [
          ...(note.startTime ? [parseZoned(note.startTime).timeZoneId] : []),
          ...(note.endTime ? [parseZoned(note.endTime).timeZoneId] : []),
        ];
      } catch {
        return [];
      }
    }),
  ];
  return normalizeTripTimezoneIds(defaultTimezone, values);
}
